import { ProportionalityRuleEnum } from './ScheduleRequests'
import type { ValidationRole } from '@/types/approval-flow.types'

export interface CreateExceptionPayload {
  paymentTiming: 'IMMEDIATE' | 'FLOW'
  proportionalityRule: 'INTEGRAL' | 'PROPORTIONAL_WITH_LIMIT' | 'PROPORTIONAL_WITHOUT_LIMIT'
  workJourney: {
    startHour: string
    endHour: string
  }
  exceptionalHourlyValue?: string
  exceptionalFullJourneyValue?: string
  justification: string
  scheduleId: number
}

export enum ExceptionStatusEnum {
  AWAITING_APPROVAL = 'Aguardando aprovação',
  APPROVED = 'Aprovado',
  REJECTED = 'Recusado',
  REVERTED = 'Revertido',
  CANCELED = 'Cancelado'
}

export interface DoctorStationExceptionRequest {
  id: number
  status: ExceptionStatusEnum
  requestDate: string
  evaluationDate: string | null
  requester: {
    id: number
    name: string
    email: string
    avatarUrl: string | null
  }
  evaluator: {
    id: number
    name: string
    email: string
    avatarUrl: string | null
  } | null
  approvalInstance?: ApprovalInstanceDto | null
}

export interface ListExceptionResponse {
  data: DoctorStationExceptionRequest[]
  count: number
}

export interface ListSingleExceptionResponse {
  data: ExceptionRequest[]
  count: number
}

export interface User {
  id: number
  name: string
  email: string
  avatarUrl: string | null
}

export interface CRM {
  number: string
  uf: string
}

export interface Doctor {
  id: number
  name: string
  crm: CRM
}

export interface Position {
  id: number
  name: string
}

export interface HealthUnit {
  id: number
  name: string
}

export interface Schedule {
  id: number
  name: string
  healthUnit: HealthUnit
}

export interface Station {
  id: number
  position: Position
  schedule: Schedule
}

export interface DoctorStation {
  id: number
  date: string
  station: Station
}

export interface ExceptionRequest {
  exceptionId: number
  status: ExceptionStatusEnum
  requesterJustification: string
  evaluatorJustification: string | null
  estimatedLoss: string
  createdAt: string
  evaluationDate: string | null
  createdBy: User
  evaluatedBy: User | null
  doctor: Doctor
  doctorStation: DoctorStation
  financials: Financials
  approvalInstance?: ApprovalInstanceDto | null
}

export interface Requester {
  id: number
  name: string
}

export interface WorkHour {
  startHour: string
  endHour: string
}

export interface WorkJourney {
  original: WorkHour
  requested: WorkHour
}

export interface RepassMode {
  original: 'FLOW' | 'IMMEDIATE'
  requested: 'FLOW' | 'IMMEDIATE'
}

export interface ProportionalityRule {
  original: ProportionalityRuleEnum
  requested: ProportionalityRuleEnum
}

export interface Changes {
  workJourney?: WorkJourney | null
  repassMode?: RepassMode | null
  proportionalityRule?: ProportionalityRule
  hourlyValue?: {
    original: string
    requested: string
  }
}

export interface Financials {
  baseCost: string
  exceptionalCost: string
  estimatedLoss: string
  baseDurationHours: string
  exceptionalDurationHours: string
  lostHours: string
  valueOfLostHours: string
  estimatedLossTotal: string
  totalExceptionalValue: string
}

export interface ApprovalDecisionDto {
  layerOrder: number
  role: string
  decision: 'PENDING' | 'APPROVED' | 'REJECTED'
  userId: number | null
  userName: string | null
  justification: string | null
  decidedAt: string | null
}

export interface ApprovalInstanceDto {
  id: number
  status: 'IN_PROGRESS' | 'COMPLETED' | 'REJECTED'
  currentLayerOrder: number
  layers: {
    order: number
    operator: 'AND' | 'OR'
    roles: { role: string; weight: number }[]
  }[]
  decisions: ApprovalDecisionDto[]
}

export interface ExceptionSummary {
  id: number
  status: ExceptionStatusEnum
  requestDate: string
  requester: Requester
  justification: string
  doctorStation: {
    id: number
    hasApprovedException: boolean
  }
  evaluator?: {
    id: number
    name: string
  }
  approver?: {
    id: number
    name: string
  }
  evaluationDate?: string | null
  approvalDate?: string
  changes: Changes
  financials: Financials
  approvalInstance?: ApprovalInstanceDto | null
  availableRolesForCurrentUser?: ValidationRole[]
}

export enum ExceptionActionType {
  VIEW = 'view',
  CANCEL = 'cancel',
  REVERT = 'revert'
}

export enum ModalActionType {
  APPROVE = 'approved',
  REJECT = 'rejected'
}

export interface ExceptionStatusSummaryResponse {
  awaitingApproval: number
  approved: number
  rejected: number
}

export interface SimulateExceptionPayload {
  paymentTiming?: 'IMMEDIATE' | 'FLOW'
  proportionalityRule?: 'INTEGRAL' | 'PROPORTIONAL_WITH_LIMIT' | 'PROPORTIONAL_WITHOUT_LIMIT'
  workJourney?: {
    startHour: string
    endHour: string
  }
  exceptionalHourlyValue?: string
  exceptionalFullJourneyValue?: string
  scheduleId?: number
}

export interface SimulateFinancialsResult {
  baseDurationHours: string
  exceptionalDurationHours: string
  lostHours: string
  valueOfLostHours: string | null
  baseCost: string | null
  exceptionalCost: string | null
  estimatedLoss: string | null
  estimatedLossTotal: string | null
  totalExceptionalValue: string | null
}

export interface SimulateExceptionResponse {
  financials: SimulateFinancialsResult
}
